/**
 * Field work, on its own.
 *
 * Every deployment, not only the caller's — who is out, where, and when is a
 * shared operational fact, and a screen showing only your own would answer the
 * wrong question.
 *
 * Since 21 Oct 2026 a deployment is its OWN record, not a request (Jeremy: "it's
 * its own thing"): edited in place, with notes and a history. Since 27 Oct 2026
 * it is booked when saved and has a PM, chosen on the form; nobody approves it. The list is the screen; raising one is a button on it,
 * because looking one up is the common act and creating one is occasional.
 */
import { mdyRange, type DeploymentView, type PersonSummary } from "@scheduler/shared";
import { useCallback, useEffect, useState } from "react";

import { ApiError, useSchedulerApi } from "../api/scheduler";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { navigate } from "../router";
import { DeploymentForm } from "./DeploymentForm";
import { setFlash, toldText } from "./flash";
import { StatusPill } from "./StatusPill";

/** Upcoming first, then everything past. A deployment last month is reference. */
function split(all: DeploymentView[]): { live: DeploymentView[]; past: DeploymentView[] } {
  const today = new Date().toISOString().slice(0, 10);
  const live: DeploymentView[] = [];
  const past: DeploymentView[] = [];
  for (const d of all) (d.end >= today ? live : past).push(d);
  // Soonest first among the upcoming; most recent first among the past.
  live.sort((a, b) => a.start.localeCompare(b.start));
  return { live, past };
}

export function FieldWork(_props: { me: PersonSummary }) {
  const api = useSchedulerApi();
  const [all, setAll] = useState<DeploymentView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showPast, setShowPast] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setAll(await api.deployments());
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [api]);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    void refresh();
  }, [refresh, back]);

  if (error) return <div className="error">{error}</div>;
  if (!all) return <p className="muted">Loading…</p>;

  const { live, past } = split(all);

  return (
    <>
      <div className="fieldbar">
        <div>
          <h2>Field work</h2>
          <p className="lede" style={{ margin: "2px 0 0" }}>
            {live.length === 0
              ? "Nothing is deployed or booked."
              : `${live.length} deployment${live.length === 1 ? "" : "s"} upcoming or under way`}
          </p>
        </div>
        {adding ? null : (
          <button type="button" className="primary" onClick={() => setAdding(true)}>New deployment</button>
        )}
      </div>

      {adding ? (
        <DeploymentForm
          onCancel={() => setAdding(false)}
          onSaved={(made) => { setFlash(made.id, toldText(made)); navigate({ kind: "deployment", id: made.id }); }}
        />
      ) : null}

      {live.length === 0 && !adding ? (
        <div className="note">
          No field work booked. <strong>New deployment</strong> raises one.
        </div>
      ) : (
        live.map((d) => <DeploymentRow key={d.id} deployment={d} />)
      )}

      {past.length > 0 ? (
        <>
          <button type="button" className="ghost" style={{ marginTop: 14 }} onClick={() => setShowPast((v) => !v)}>
            {showPast ? "Hide" : "Show"} {past.length} past deployment{past.length === 1 ? "" : "s"}
          </button>
          {showPast ? past.map((d) => <DeploymentRow key={d.id} deployment={d} past />) : null}
        </>
      ) : null}
    </>
  );
}

function DeploymentRow({ deployment, past }: { deployment: DeploymentView; past?: boolean }) {
  const crew = deployment.crew;
  return (
    <button
      type="button"
      className={`deployrow${past ? " past" : ""}`}
      onClick={() => navigate({ kind: "deployment", id: deployment.id })}
    >
      <span className="deploy-main">
        <span className="deploy-title">{deployment.title}</span>
        <span className="deploy-crew">
          {crew.length} on it — {crew.map((p) => p.name).join(", ")}
          {deployment.site ? ` · ${deployment.site}` : ""}
        </span>
        <span className="deploy-crew">
          {deployment.project ? deployment.project.name : "No job"}
          {" · "}{deployment.pm ? `PM ${deployment.pm.name}` : "No PM"}
        </span>
      </span>
      <span className="deploy-when">
        <span className="dates">
          {mdyRange(deployment.start, deployment.end)}
        </span>
        <StatusPill status={deployment.status} />
      </span>
    </button>
  );
}
